import { Controller } from "@hotwired/stimulus"
import { request } from "./utils/api"
import {
  normalizeHexColor,
  normalizeCssColor,
  matchFontFamily,
  escapeHtml
} from "./utils/color"

// A "selected" target and its base target share one element — the active chip is
// `class="category-link category-link-active"`, the active tab is
// `class="modal-tab-button ... active"`. In the saved stylesheet the selected
// rule still wins on that element (higher specificity, or a later position), but
// a preview rule is injected with !important and would override it, so editing
// the base target visibly repaints the selected one too. Excluding the variant
// keeps the preview faithful to what saving actually produces.
//
// Keyed by base accessor; values are the class to exclude from it. Pseudo
// targets (".category-link:hover") are deliberately absent — there the base
// really does outrank the selected rule in the saved CSS, so the bleed is
// correct and the preview should show it.
// Shown in a panel's badge when the selected target has no stored value for that
// property. The editor used to substitute the menu container's colour here, which
// read as "this chip is white" and then got saved as if the user had chosen it.
const UNSET_LABEL = "Not set"

const PREVIEW_STYLE_ID = "design-theme-preview-style"
const HIGHLIGHT_STYLE_ID = "design-target-highlight-style"

const SELECTED_VARIANT_EXCLUSIONS = {
  ".menu-link": ".menu-link-active",
  ".category-link": ".category-link-active",
  ".modal-tab-button": ".active"
}

// The design editor (wizard "step 3"): the sidebar panels for colours,
// typography, images and navigation, plus the live preview and save. The setup
// wizard (global/custom + theme) lives in design_wizard_controller.js.
export default class extends Controller {
  static targets = [
    "navModal",
    "navModalTitle",
    "navModalImage",
    "colorField",
    "fontFamilyInput",
    "fontFamilyValue",
    "fontFamilyTargetInput",
    "imageInput",
    "imagePreview",
    "imageValue",
    "navItem",
    "navScroll",
    "sidebar",
    "subpanel",
    "panel",
    "themeSaveButton",
    "themeStatus",
    "themeModal",
    "themeConfirm",
    "themeConfirmCopy",
    "themeApplyButton"
  ]
  static values = {
    themeUrl: String,
    fontFamilyMap: Object,
    navPreviewMap: Object,
    themePresetUrl: String,
    themeScope: String,
    restaurantDesignUrl: String,
    csrfToken: String
  }

  navPreviewTimer = null

  // Which preset the confirm step is currently asking about, or null when it is
  // closed. Applying is deliberately two-step: it rewrites colours in place.
  pendingThemePreset = null

  // Every edit the user has made since the last save, keyed `${property}|${target}`.
  //
  // Keying by target is what lets an edit survive switching the "Apply to"
  // dropdown: the editor used to hold one value per property, so moving the
  // dropdown threw the pending edit away and the colour visibly reverted. Save
  // posts this whole map, so several targets can be styled in one pass.
  //
  // It also means a save only ever writes what the user actually touched. The
  // editor previously sent all three properties every time, so saving a category
  // background quietly wrote a font colour and font family to whichever targets
  // those dropdowns happened to be left on - usually `.menu-background`.
  pendingChanges = new Map()

  connect() {
    this.pseudoPreviewRules = {}
    this.imagePreviewKeys = []
    this.imageObjectUrls = {}
    this.highlightStatusTimer = null
    this.pendingChanges.clear()
    this.closeThemePresets()
    this.closePanel()
    window.requestAnimationFrame(() => this.hydrateColorFields())

    this.viewportHandler = () => this.syncSidebarViewport()
    window.addEventListener("resize", this.viewportHandler)
    window.addEventListener("scroll", this.viewportHandler, { passive: true })
    this.syncSidebarViewport()
  }

  disconnect() {
    window.removeEventListener("resize", this.viewportHandler)
    window.removeEventListener("scroll", this.viewportHandler)
  }

  syncSidebarViewport() {
    this.updateSidebarHeight()
    this.updateNavScrollFade()
  }

  updateSidebarHeight() {
    if (!this.hasSidebarTarget) return

    const bottomGap = 16
    const available = window.innerHeight - this.sidebarTarget.getBoundingClientRect().top - bottomGap
    this.sidebarTarget.style.maxHeight = `${Math.max(available, 200)}px`
  }

  updateNavScrollFade() {
    if (!this.hasNavScrollTarget) return

    const element = this.navScrollTarget
    const canScrollUp = element.scrollTop > 1
    const canScrollDown = element.scrollTop + element.clientHeight < element.scrollHeight - 1

    element.classList.toggle("can-scroll-up", canScrollUp)
    element.classList.toggle("can-scroll-down", canScrollDown)
  }

  fontFamilyChanged() {
    const fontFamily = this.fontFamilyInputTarget.value

    this.styleChanged("font-family", this.currentFontFamilyTarget(), fontFamily)
    this.setFontFamily(fontFamily)
  }

  // Every colour row carries the target and property it writes, so one handler
  // serves the swatch and the hex box on all of them. Each row shows its own
  // value, which is why styleChanged no longer updates a display: with a field
  // per target there is no shared badge to keep in step, and so nothing for an
  // untouched target to be painted with.
  //
  // Typing is only an edit once it spells a colour, so a half-typed "#ff" is left
  // alone rather than recorded and previewed.
  colorFieldChanged(event) {
    const input = event.currentTarget
    const color = normalizeHexColor(input.value)
    if (!color) return

    this.syncColorRow(input, color)
    this.styleChanged(input.dataset.styleProperty, input.dataset.styleTarget, color)
  }

  colorFieldFocused(event) {
    this.highlightDesignTarget(event.currentTarget.dataset.styleTarget)
  }

  // Both inputs in a row are two views of one value, and a row that has been given
  // a value is no longer showing an inherited one.
  syncColorRow(input, color) {
    const row = input.closest(".design-color-row")
    if (!row) return

    const swatch = row.querySelector(".design-color-row-swatch")
    const text = row.querySelector(".design-color-row-text")

    if (swatch && swatch !== input) swatch.value = color
    if (text && text !== input) text.value = color
    row.classList.remove("is-inherited", "is-unset")
  }

  toggleColorGroup(event) {
    const toggle = event.currentTarget
    const body = toggle.nextElementSibling
    const isOpen = toggle.getAttribute("aria-expanded") === "true"

    toggle.setAttribute("aria-expanded", String(!isOpen))
    if (body) body.hidden = isOpen
  }

  // A row the server left blank has nothing stored for that target, but the target
  // still renders as *something* - the global stylesheet, or a theme preset applied
  // before these rows existed. Showing that colour is what makes the panel readable;
  // recording it is what EM-2569 removed, because it saves a colour nobody chose. So
  // this fills the inputs and marks the row inherited, and leaves pendingChanges
  // alone. Only a real edit promotes a row to pending.
  //
  // Pseudo targets are skipped: computedStyleValueFor strips the pseudo and would
  // report the base element's colour, which is not what ":hover" renders as.
  hydrateColorFields() {
    this.colorFieldTargets.forEach((swatch) => {
      const row = swatch.closest(".design-color-row")
      const text = row && row.querySelector(".design-color-row-text")
      if (!row || !text || text.value) return

      const { styleTarget, styleProperty } = swatch.dataset
      if (this.isPseudoTarget(styleTarget)) return

      const color = normalizeHexColor(this.computedStyleValueFor(styleTarget, styleProperty))
      if (!color) return

      swatch.value = color
      text.value = color
      row.classList.add("is-inherited")
      row.classList.remove("is-unset")
    })
  }

  // One edit, recorded and previewed against one target. Each panel used to call a
  // shared handler that re-previewed all three properties at their current targets,
  // which is what let an untouched panel paint (and then save) a value of its own.
  styleChanged(property, target, value) {
    this.recordPendingChange(property, target, value)
    this.applyPreviewProperty(target, property, value)
    this.markThemeUnsaved()
  }

  pendingChangeKey(property, target) {
    return `${property}|${target}`
  }

  recordPendingChange(property, target, value) {
    this.pendingChanges.set(this.pendingChangeKey(property, target), { property, target, value })
  }

  pendingValueFor(property, target) {
    const change = this.pendingChanges.get(this.pendingChangeKey(property, target))

    return change ? change.value : null
  }

  // What the typography panel should show for a target: the user's unsaved edit if
  // there is one, otherwise what is actually stored, otherwise nothing. The colour
  // rows do not go through here - each one holds its own value.
  displayValueFor(property, target) {
    const pending = this.pendingValueFor(property, target)
    if (pending !== null) return pending

    return this.savedFontFamilyFor(target)
  }

  imageChanged(event) {
    const input = event.currentTarget
    const file = input.files && input.files[0]
    if (!file) return

    const optionId = input.dataset.imageOptionId
    const cssAccessor = input.dataset.imageCssAccessor
    const imageUrl = URL.createObjectURL(file)

    this.revokeImageObjectUrl(optionId)
    this.imageObjectUrls[optionId] = imageUrl
    this.renderImagePreview(optionId, imageUrl)
    this.setImageStatus(optionId, "Unsaved")
    this.applyImagePreview(cssAccessor, imageUrl)
    this.markThemeUnsaved()
  }

  previewNavigation(event) {
    event.preventDefault()

    const form = event.currentTarget
    const value = form.dataset.navValue
    const label = form.dataset.navLabel

    this.showNavPreview(value, label)

    window.clearTimeout(this.navPreviewTimer)
    this.navPreviewTimer = window.setTimeout(() => {
      form.submit()
    }, 5000)
  }

  showNavPreview(value, label) {
    if (!this.hasNavModalTarget) return

    if (this.hasNavModalTitleTarget) {
      this.navModalTitleTarget.textContent = `${label} navigation`
    }

    const imageUrl = this.navPreviewMapValue[value]
    if (this.hasNavModalImageTarget && imageUrl) {
      this.navModalImageTarget.src = imageUrl
      this.navModalImageTarget.alt = `${label} navigation preview`
    }

    this.navModalTarget.hidden = false
  }

  hideNavPreview() {
    if (this.hasNavModalTarget) this.navModalTarget.hidden = true
  }

  openThemePresets(event) {
    if (event) event.preventDefault()

    this.resetThemePresetConfirm()
    if (this.hasThemeModalTarget) this.themeModalTarget.hidden = false
  }

  closeThemePresets(event) {
    if (event) event.preventDefault()

    this.resetThemePresetConfirm()
    if (this.hasThemeModalTarget) this.themeModalTarget.hidden = true
  }

  selectThemePreset(event) {
    event.preventDefault()

    const option = event.currentTarget
    this.pendingThemePreset = option.dataset.theme
    if (!this.pendingThemePreset) return

    if (this.hasThemeConfirmCopyTarget) {
      this.themeConfirmCopyTarget.textContent = this.themePresetWarning(option.dataset.themeLabel || this.pendingThemePreset)
    }
    if (this.hasThemeConfirmTarget) this.themeConfirmTarget.hidden = false
  }

  cancelThemePreset(event) {
    if (event) event.preventDefault()

    this.resetThemePresetConfirm()
  }

  // With Brand Studio, a theme on the restaurant design is the restaurant's
  // look and on a custom design this menu's own; either way the colours set in
  // this editor can stay (and still win) or be replaced. Without it, a preset
  // writes into the same block the colour panels write to. Applying reloads,
  // which discards anything still pending - worth saying before it happens.
  themePresetWarning(label) {
    const base = {
      restaurant: `Apply ${label} to your restaurant? Menus using the restaurant design and your landing page will change. Keep the colours you set here, or replace them?`,
      menu: `Apply ${label} to this menu only? Keep the colours you set here, or replace them?`
    }[this.themeScopeValue] || `Apply ${label}? This replaces the colours set for this menu.`
    if (this.pendingChanges.size === 0) return base

    return `${base} Your unsaved changes will be lost.`
  }

  resetThemePresetConfirm() {
    this.pendingThemePreset = null
    if (this.hasThemeConfirmTarget) this.themeConfirmTarget.hidden = true
    this.setThemeApplyDisabled(false)
  }

  setThemeApplyDisabled(disabled) {
    this.themeApplyButtonTargets.forEach((button) => { button.disabled = disabled })
  }

  applyThemePreset(event) {
    if (event) event.preventDefault()
    if (!this.pendingThemePreset || !this.hasThemePresetUrlValue) return

    const mode = event?.currentTarget?.dataset?.mode
    this.setThemeApplyDisabled(true)
    this.setThemeStatus("Applying theme...")

    return request(this.themePresetUrlValue, {
      method: "POST",
      body: mode ? { theme: this.pendingThemePreset, mode: mode } : { theme: this.pendingThemePreset },
      csrfToken: this.csrfTokenValue
    }).then((response) => {
      if (!response.ok) throw new Error(`apply_theme_preset failed: ${response.status}`)

      // Reload rather than patching the preview: a palette touches ~25 targets at
      // once, and the server re-renders the editor from the styles it just wrote.
      // This is how the setup wizard finishes too.
      window.location.reload()
    }).catch(() => {
      this.setThemeApplyDisabled(false)
      this.setThemeStatus("Could not apply theme")
    })
  }

  // Back to the restaurant design: the menu is reassigned to it outright.
  useRestaurantDesign(event) {
    if (event) event.preventDefault()
    if (!this.hasRestaurantDesignUrlValue) return

    this.setThemeStatus("Switching to the restaurant design...")
    return request(this.restaurantDesignUrlValue, { method: "POST", csrfToken: this.csrfTokenValue })
      .then((response) => {
        if (!response.ok) throw new Error(`choose_edit_mode failed: ${response.status}`)
        window.location.reload()
      })
      .catch(() => this.setThemeStatus("Could not switch to the restaurant design"))
  }

  switchPanel(event) {
    event.preventDefault()
    const panelName = event.currentTarget.dataset.panel
    const isAlreadyActive = event.currentTarget.classList.contains("active")

    if (isAlreadyActive) {
      this.closePanel()
    } else {
      this.activatePanel(panelName)
    }
  }

  closeSubnav(event) {
    if (event) event.preventDefault()
    this.closePanel()
  }

  // Switching the dropdown is navigation, not an edit. The preview rule for the
  // target being left stays up and its entry stays in pendingChanges, so the
  // font the user just chose neither disappears nor goes unsaved. Nothing is
  // previewed for the target being arrived at either - showing it under
  // !important would paint a value the user has not asked for.
  fontFamilyTargetChanged() {
    const target = this.currentFontFamilyTarget()

    this.setFontFamily(this.displayValueFor("font-family", target))
    this.highlightDesignTarget(target)
  }

  saveTheme(event) {
    if (event) event.preventDefault()

    this.themeSaveButtonTarget.disabled = true
    this.themeSaveButtonTarget.setAttribute("aria-busy", "true")
    this.setThemeStatus("Saving...")

    const savedChanges = Array.from(this.pendingChanges.values())

    return request(this.themeUrlValue, {
      method: "PATCH",
      body: this.themeRequestBody(),
      csrfToken: this.csrfTokenValue
    }).then((response) => response.json()).then((data) => {
      const styleTag = document.getElementById("menu-style")
      if (styleTag && data.css) styleTag.innerHTML = data.css

      // Retire exactly the edits this request carried, and mirror them into the
      // saved maps so the panels keep showing what is really stored. An edit made
      // while the request was in flight is a different object under the same key,
      // so it stays pending rather than being marked saved.
      savedChanges.forEach((change) => {
        const key = this.pendingChangeKey(change.property, change.target)

        this.recordSavedValue(change)
        if (this.pendingChanges.get(key) === change) this.pendingChanges.delete(key)
      })

      this.clearPreviewRulesFor(savedChanges)
      this.clearSavedImagePreviews(data.image_urls || {})
      this.setThemeStatus("Saved")
      this.themeSaveButtonTarget.textContent = "Saved"
      window.setTimeout(() => {
        this.themeSaveButtonTarget.textContent = "Save Design"
      }, 1200)
    }).catch(() => {
      this.setThemeStatus("Could not save")
    }).finally(() => {
      this.themeSaveButtonTarget.disabled = false
      this.themeSaveButtonTarget.removeAttribute("aria-busy")
    })
  }

  themeRequestBody() {
    const changes = Array.from(this.pendingChanges.values())

    if (!this.hasImageInputTarget || !this.imageInputTargets.some((input) => input.files && input.files.length > 0)) {
      return changes.length > 0 ? { style_changes: changes } : {}
    }

    // A save that carries image uploads has to be multipart, so the same list
    // travels as a JSON string; update_design_theme accepts either shape.
    const formData = new FormData()
    if (changes.length > 0) formData.append("style_changes", JSON.stringify(changes))

    this.imageInputTargets.forEach((input) => {
      const file = input.files && input.files[0]
      if (!file) return

      formData.append(`menu_style_images[${input.dataset.imageOptionId}]`, file)
    })

    return formData
  }

  // Only the typography panel reads back from a saved map; the colour rows each
  // hold their own value and already show what the save wrote.
  recordSavedValue({ property, target, value }) {
    if (property !== "font-family") return

    this.fontFamilyMapValue = { ...this.fontFamilyMapValue, [target]: value }
  }

  // The response carries a stylesheet that now contains these declarations, so the
  // !important preview overlay for them is redundant. Dropping it stops the overlay
  // accumulating a rule per target for the whole session, and keeps what is on
  // screen driven by the saved CSS rather than by the editor.
  clearPreviewRulesFor(changes) {
    changes.forEach(({ property, target }) => {
      delete this.pseudoPreviewRules[`${target}|${property}`]
    })

    this.renderPseudoPreviewStyles()
  }

  setFontFamily(fontFamily) {
    const hasOption = Array.from(this.fontFamilyInputTarget.options).some((option) => option.value === fontFamily)
    if (!hasOption) {
      this.fontFamilyValueTarget.textContent = UNSET_LABEL
      return
    }

    this.fontFamilyInputTarget.value = fontFamily
    this.fontFamilyValueTarget.textContent = this.fontFamilyInputTarget.options[this.fontFamilyInputTarget.selectedIndex].text
  }

  currentFontFamilyTarget() {
    if (!this.hasFontFamilyTargetInputTarget) return ".menu-background"

    return this.fontFamilyTargetInputTarget.value || ".menu-background"
  }

  // Stored value first, then what the target actually renders as, then nothing.
  // There is deliberately no fallback to `.menu-background`'s value: reporting the
  // menu container's font for an unstyled target is how targets used to end up
  // saved with values nobody had chosen. A null means "not set" and is shown as such.
  savedFontFamilyFor(cssAccessor) {
    return this.fontFamilyMapValue[cssAccessor] || this.computedStyleValueFor(cssAccessor, "font-family")
  }

  previewSelectorFor(cssAccessor) {
    const exclusion = SELECTED_VARIANT_EXCLUSIONS[cssAccessor]
    if (!exclusion) return `.public-menu ${cssAccessor}`

    return `.public-menu ${cssAccessor}:not(${exclusion})`
  }

  highlightSelectorFor(cssAccessor) {
    return this.previewSelectorFor(cssAccessor.replace(/:(hover|focus|active)$/, ""))
  }

  highlightDesignTarget(cssAccessor) {
    const selector = this.highlightSelectorFor(cssAccessor)

    this.renderTargetHighlightStyles(selector)
    this.scrollHighlightedTargetIntoView(selector)
  }

  renderTargetHighlightStyles(selector) {
    let styleTag = document.getElementById(HIGHLIGHT_STYLE_ID)
    if (!styleTag) {
      styleTag = document.createElement("style")
      styleTag.id = HIGHLIGHT_STYLE_ID
      document.head.appendChild(styleTag)
    }

    styleTag.innerHTML = `
      @keyframes design-target-highlight-pulse {
        0%, 100% {
          outline-color: #32b6c5;
          box-shadow: 0 0 0 5px rgba(50, 182, 197, 0.22), 0 14px 34px rgba(15, 23, 42, 0.2);
        }
        50% {
          outline-color: #f4b740;
          box-shadow: 0 0 0 8px rgba(244, 183, 64, 0.2), 0 18px 42px rgba(15, 23, 42, 0.24);
        }
      }

      ${selector} {
        outline: 3px solid #32b6c5 !important;
        outline-offset: 4px !important;
        position: relative !important;
        z-index: 30 !important;
        animation: design-target-highlight-pulse 1.5s ease-in-out infinite !important;
      }
    `
  }

  scrollHighlightedTargetIntoView(selector) {
    const target = this.visibleElementForSelector(selector)
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
      return
    }

    this.showTemporaryThemeStatus("Target will highlight when it is visible")
  }

  visibleElementForSelector(selector) {
    let elements = []

    try {
      elements = Array.from(document.querySelectorAll(selector))
    } catch (_error) {
      return null
    }

    return elements.find((element) => {
      const rect = element.getBoundingClientRect()
      const style = window.getComputedStyle(element)

      return rect.width > 0 && rect.height > 0 && style.visibility !== "hidden" && style.display !== "none"
    }) || null
  }

  computedStyleValueFor(cssAccessor, property) {
    const target = this.visibleElementForSelector(this.highlightSelectorFor(cssAccessor))
    if (!target) return null

    const value = this.withEditorOverlaysSuspended(() => {
      return window.getComputedStyle(target).getPropertyValue(property).trim()
    })
    if (!value) return null

    if (property === "color" || property === "background-color") return normalizeCssColor(value)
    if (property === "font-family") return matchFontFamily(value, this.fontFamilyOptionValues())

    return value
  }

  // The preview and highlight layers are injected with !important, so reading a
  // computed style with them live reports whatever the editor last painted rather
  // than what is stored. Disabling them for the read makes the answer depend on the
  // saved stylesheet alone, instead of on which chips happen to be on screen and
  // what was previewed a moment ago. getComputedStyle is synchronous, so nothing
  // is visible to the user in between.
  withEditorOverlaysSuspended(callback) {
    const overlays = [PREVIEW_STYLE_ID, HIGHLIGHT_STYLE_ID]
      .map((id) => document.getElementById(id))
      .filter((styleTag) => styleTag && !styleTag.disabled)

    overlays.forEach((styleTag) => { styleTag.disabled = true })

    try {
      return callback()
    } finally {
      overlays.forEach((styleTag) => { styleTag.disabled = false })
    }
  }

  fontFamilyOptionValues() {
    return Array.from(this.fontFamilyInputTarget.options).map((option) => option.value)
  }

  applyPreviewProperty(cssAccessor, property, value) {
    this.pseudoPreviewRules[`${cssAccessor}|${property}`] = {
      selector: this.previewSelectorFor(cssAccessor),
      property: property,
      value: value
    }
    this.renderPseudoPreviewStyles()
  }

  applyImagePreview(cssAccessor, imageUrl) {
    const key = `${cssAccessor}|background-image`
    if (!this.imagePreviewKeys.includes(key)) this.imagePreviewKeys.push(key)

    this.pseudoPreviewRules[key] = {
      selector: this.previewSelectorFor(cssAccessor),
      property: "background-image",
      value: `url("${imageUrl}")`
    }
    this.renderPseudoPreviewStyles()
  }

  isPseudoTarget(cssAccessor) {
    return cssAccessor.includes(":")
  }

  clearPseudoPreview(cssAccessor, property) {
    delete this.pseudoPreviewRules[`${cssAccessor}|${property}`]
    this.renderPseudoPreviewStyles()
  }

  renderPseudoPreviewStyles() {
    let styleTag = document.getElementById(PREVIEW_STYLE_ID)
    if (!styleTag) {
      styleTag = document.createElement("style")
      styleTag.id = PREVIEW_STYLE_ID
      document.head.appendChild(styleTag)
    }

    styleTag.innerHTML = Object.values(this.pseudoPreviewRules).map((rule) => {
      return `${rule.selector} { ${rule.property}: ${rule.value} !important; }`
    }).join("\n")
  }

  clearFontColorPreview(cssAccessor) {
    if (this.isPseudoTarget(cssAccessor)) {
      this.clearPseudoPreview(cssAccessor, "color")
      return
    }

    document.querySelectorAll(this.previewSelectorFor(cssAccessor)).forEach((element) => {
      element.style.removeProperty("color")
    })
  }

  clearBackgroundColorPreview(cssAccessor) {
    if (this.isPseudoTarget(cssAccessor)) {
      this.clearPseudoPreview(cssAccessor, "background-color")
      return
    }

    document.querySelectorAll(this.previewSelectorFor(cssAccessor)).forEach((element) => {
      element.style.removeProperty("background-color")
    })
  }

  clearFontFamilyPreview(cssAccessor) {
    if (this.isPseudoTarget(cssAccessor)) {
      this.clearPseudoPreview(cssAccessor, "font-family")
      return
    }

    document.querySelectorAll(this.previewSelectorFor(cssAccessor)).forEach((element) => {
      element.style.removeProperty("font-family")
    })
  }

  setThemeStatus(message) {
    if (!this.hasThemeStatusTarget) return

    this.themeStatusTarget.textContent = message
  }

  showTemporaryThemeStatus(message) {
    if (!this.hasThemeStatusTarget) return

    window.clearTimeout(this.highlightStatusTimer)
    this.setThemeStatus(message)
    this.highlightStatusTimer = window.setTimeout(() => {
      this.setThemeStatus("Preview only - not saved")
    }, 1800)
  }

  renderImagePreview(optionId, imageUrl) {
    const preview = this.imagePreviewTargets.find((target) => target.dataset.imageOptionId === optionId)
    if (!preview) return

    preview.innerHTML = `<img src="${escapeHtml(imageUrl)}" alt="">`
  }

  setImageStatus(optionId, message) {
    const badge = this.imageValueTargets.find((target) => target.dataset.imageOptionId === optionId)
    if (!badge) return

    badge.textContent = message
  }

  clearSavedImagePreviews(imageUrls) {
    this.imagePreviewKeys.forEach((key) => delete this.pseudoPreviewRules[key])
    this.imagePreviewKeys = []
    this.renderPseudoPreviewStyles()

    if (this.hasImageInputTarget) {
      this.imageInputTargets.forEach((input) => {
        const optionId = input.dataset.imageOptionId
        const imageUrl = imageUrls[optionId]

        input.value = ""
        this.revokeImageObjectUrl(optionId)
        if (imageUrl) this.renderImagePreview(optionId, imageUrl)
        this.setImageStatus(optionId, imageUrl ? "Current" : "Empty")
      })
    }
  }

  revokeImageObjectUrl(optionId) {
    if (!this.imageObjectUrls[optionId]) return

    URL.revokeObjectURL(this.imageObjectUrls[optionId])
    delete this.imageObjectUrls[optionId]
  }

  activatePanel(panelName) {
    if (this.hasSubpanelTarget) this.subpanelTarget.hidden = false

    this.navItemTargets.forEach((item) => {
      const isActive = item.dataset.panel === panelName

      item.classList.toggle("active", isActive)
      item.setAttribute("aria-expanded", isActive ? "true" : "false")
    })

    this.panelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.panelName !== panelName
    })

    this.updateNavScrollFade()
  }

  closePanel() {
    if (this.hasSubpanelTarget) this.subpanelTarget.hidden = true
    this.navItemTargets.forEach((item) => {
      item.classList.remove("active")
      item.setAttribute("aria-expanded", "false")
    })
    this.panelTargets.forEach((panel) => {
      panel.hidden = true
    })

    this.updateNavScrollFade()
  }

  markThemeUnsaved() {
    this.setThemeStatus("Preview only - not saved")
    this.themeSaveButtonTarget.textContent = "Save Design"
  }
}
